import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

/**
 * EmailVerificationPending - Shown when user is authenticated but email is not verified
 * Provides clear instructions and ability to resend verification email
 */
const EmailVerificationPending = () => {
  const { user, logout } = useAuth0();
  const { t } = useTranslation();
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  const userEmail = user?.email || '';

  const handleResendEmail = async () => {
    if (!userEmail) {
      setMessage(t('emailVerification.noEmailError', 'No email address found. Please try logging in again.'));
      setMessageType('error');
      return;
    }

    setIsResending(true);
    setMessage('');

    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/verification/resend-verification-email`,
        { email: userEmail }
      );
      setMessage(t('emailVerification.resendSuccess', 'Verification email sent! Please check your inbox.'));
      setMessageType('success');
    } catch (error) {
      console.error('Error resending verification email:', error);
      const errorMessage =
        error.response?.data?.message ||
        t('emailVerification.resendError', 'Failed to resend verification email. Please try again.');
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setIsResending(false);
    }
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <div>
      <main>
        <section className="section background-light">
          <div className="container">
            <div className="signup-wrapper">
              <div className="email-verification-pending">
                {/* Email Icon */}
                <div className="verification-icon">
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 6L12 13L2 6" />
                  </svg>
                </div>

                {/* Heading */}
                <h2 className="primary-text">
                  {t('emailVerification.heading', 'Verify Your Email')}
                </h2>

                {/* Instructions */}
                <p className="verification-instructions">
                  {t(
                    'emailVerification.instructions',
                    "We've sent a verification email to:"
                  )}
                </p>

                {/* Email Display */}
                <p className="verification-email">{userEmail}</p>

                <p className="verification-instructions">
                  {t(
                    'emailVerification.checkInbox',
                    'Please check your inbox and click the verification link to continue. If you don\'t see the email, check your spam folder.'
                  )}
                </p>

                {/* Status Message */}
                {message && (
                  <div className={`verification-message ${messageType}`}>
                    {message}
                  </div>
                )}

                {/* Actions */}
                <div className="verification-actions">
                  <button
                    className="Button primary"
                    onClick={handleResendEmail}
                    disabled={isResending}
                  >
                    {isResending
                      ? t('emailVerification.resending', 'Sending...')
                      : t('emailVerification.resendButton', 'Resend Verification Email')}
                  </button>
                </div>

                {/* Back to Login */}
                <p className="verification-footer">
                  {t('emailVerification.wrongEmail', 'Wrong email address?')}{' '}
                  <button className="link-button" onClick={handleLogout}>
                    {t('emailVerification.signOutAndRetry', 'Sign out and try again')}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <style>{`
        .email-verification-pending {
          text-align: center;
          padding: 40px 20px;
          padding-top: 100px;
          max-width: 500px;
          margin: 0 auto;
        }

        .verification-icon {
          color: #ff8264;
          margin-bottom: 24px;
        }

        .verification-icon svg {
          width: 80px;
          height: 80px;
        }

        .email-verification-pending h2 {
          margin-bottom: 16px;
          color: #343f52;
        }

        .verification-instructions {
          color: #666;
          margin-bottom: 8px;
          line-height: 1.5;
        }

        .verification-email {
          font-weight: 600;
          font-size: 1.1em;
          color: #333;
          background: #f5f5f5;
          padding: 12px 20px;
          border-radius: 8px;
          margin: 16px 0 24px;
          word-break: break-all;
        }

        .verification-message {
          padding: 12px 16px;
          border-radius: 8px;
          margin: 16px 0;
        }

        .verification-message.success {
          background: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #a5d6a7;
        }

        .verification-message.error {
          background: #ffebee;
          color: #c62828;
          border: 1px solid #ef9a9a;
        }

        .verification-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin: 24px 0;
        }

        .verification-actions .Button {
          width: 100%;
          padding: 14px 24px;
          font-size: 1em;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .verification-actions .Button.primary {
          background: linear-gradient(135deg, #ff8264 0%, #e5745a 100%);
          color: white;
          border: none;
          box-shadow: 0 4px 16px rgba(255, 130, 100, 0.3);
        }

        .verification-actions .Button.primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #e5745a 0%, #cc6650 100%);
          box-shadow: 0 6px 24px rgba(255, 130, 100, 0.4);
        }

        .verification-actions .Button.primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .verification-footer {
          color: #888;
          font-size: 0.9em;
          margin-top: 24px;
        }

        .link-button {
          background: none;
          border: none;
          color: #ff8264;
          cursor: pointer;
          text-decoration: underline;
          font-size: inherit;
          padding: 0;
        }

        .link-button:hover {
          opacity: 0.8;
        }
      `}</style>
    </div>
  );
};

export default EmailVerificationPending;
